import {
  Box,
  Typography,
  Portal,
  Fab,
  useMediaQuery,
  useTheme,
  Tooltip,
  Paper,
  alpha,
  Container,
} from "@mui/material";
// import Grid from "@mui/material/Grid";
// import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
// import ChevronLeft from "@mui/icons-material/ChevronLeft";
import { ExtractedInfoPage } from "../ExtractedInfo/ExtractedInfoPage";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useNavigate,
  useLocation,
  useParams,
  Navigate,
} from "react-router-dom";
import { EforceIncidentModel } from "../../shared/interfaces/EforceModels";
import type { JSONSchema7 } from "json-schema";
import Button from "@mui/material/Button";
import { useToast } from "../../shared/toast";
import { ErrorState, SignatureDialog } from "../../shared/ui";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React from "react";
import { FieldMapping } from "@/features/reports/config/fieldMapping";
import { useQuery } from "@tanstack/react-query";
import {
  incidentDataQuery,
  reportMetadataQuery,
} from "../../features/reports/api/reportQueries";
import { currentProfileQuery } from "../../features/users/api/profileQueries";
import { useResolvedReportSchema } from "../../features/reports/hooks/useResolvedReportSchema";
import { finalEditsGate } from "./finalEditsGate";
import { getCoComments } from "@/features/co-review/api/coReviewApi";
import { rmsClient } from "@/shared/api/clients";
import { ReportStatus } from "../../shared/interfaces/ReportStatus";
import { ResubmitConfirmationDialog } from "../../components/coReview/ResubmitConfirmationDialog";
import type { CoReviewComment } from "../../shared/interfaces/CoReviewComment";
import { usePageHeader } from "../../context/LayoutContext";
import { useOrgId } from "../../shared/auth/orgContext";
import { useAuth } from "@workos-inc/authkit-react";
import { useFeatureFlag } from "../../shared/hooks/useFeatureFlag";
import { TN_CASE_NUMBER_INPUT_FEATURE_FLAG } from "../../features/tn-case-number-input/featureFlag";
import { useCoApprovalEnabled } from "@/features/co-review/hooks/useCoApprovalEnabled";
import FactCheckIcon from "@mui/icons-material/FactCheck";

// The editor's handoff state is now an optional fast path: it is fresher
// than any fetch (it can carry the narrative-only subset, which is
// irreproducible from a by-id fetch) and renders with zero flash. On
// refresh/deep-link the page falls back to fetching by reportID.
type FinalEditsState = {
  modelData?: EforceIncidentModel;
  showRMSCommit?: boolean;
  jsonSchema?: JSONSchema7;
  schemaMapping?: Record<string, FieldMapping>;
};

export const FinalEditsPage: React.FC = () => {
  const location = useLocation();
  // Signer identity for the signature dialog (previously read inside
  // ReportSubmitModal).
  const { data: profileData } = useQuery(currentProfileQuery());
  const userProfile = profileData ?? null;
  // The signature dialog requires typing this exact string — a null profile
  // must not produce "undefined undefined".
  const signerDisplayName =
    [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(" ") ||
    "Unknown User";
  const navigate = useNavigate();
  const { reportID } = useParams(); // top-level hook use
  const theme = useTheme();
  // Set page header in app bar
  usePageHeader({
    title: "Final Edits",
    subtitle: "Review and confirm your report before submission",
    icon: <FactCheckIcon />,
  });

  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const organizationId = useOrgId();
  const { user } = useAuth();
  const caseNumberInputEnabled = useFeatureFlag(TN_CASE_NUMBER_INPUT_FEATURE_FLAG);
  const coApprovalEnabled = useCoApprovalEnabled();

  const [isLoading, setIsLoading] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [allEasterEggsFound, setAllEasterEggsFound] = useState(false);
  const [coComments, setCoComments] = useState<CoReviewComment[]>([]);
  const [resubmitDialogOpen, setResubmitDialogOpen] = useState(false);
  // Which action the submit modal confirms. Tracked as explicit state because
  // comparing handleSubmitFunction.current against handleRMSCommit by identity
  // fails — the handler is recreated on every render, so the comparison was
  // always false and the modal showed the "sign" copy on the RMS-commit path.
  const [submitMode, setSubmitMode] = useState<"sign" | "rmsCommit">("sign");
  const toast = useToast();

  const state = location.state as FinalEditsState | undefined;

  const incidentModelRef = useRef<EforceIncidentModel | null>(
    state?.modelData || null
  );
  const handleSubmitFunction = useRef<(caseNumber?: string) => void>(() => {});

  // Metadata gates ownership/status and supplies the rms fallback; the
  // incident + schema queries only run on the fetch path (no handoff state).
  const metadataQuery = useQuery({
    ...reportMetadataQuery(reportID ?? ""),
    enabled: !!reportID,
  });
  const incidentQuery = useQuery({
    ...incidentDataQuery(reportID ?? ""),
    enabled: !!reportID && !state?.modelData,
  });
  const fetchedModel =
    (incidentQuery.data?.reportContent as EforceIncidentModel | undefined) ??
    undefined;
  const reportStatus = metadataQuery.data?.reportStatus ?? "";
  const showRMSCommit =
    state?.showRMSCommit ??
    (metadataQuery.data
      ? ![null, "", "KAR"].includes(metadataQuery.data.rms)
      : false);
  const rms = metadataQuery.data ? metadataQuery.data.rms || "KAR" : null;

  // Editor-parity schema pipeline (full $ref resolve + oneOf→enum,
  // reportType-specific, unsliced) — only when the handoff didn't carry one.
  const schemaHook = useResolvedReportSchema({
    enabled: !state?.jsonSchema && !!incidentQuery.data?.reportType,
    reportType: incidentQuery.data?.reportType,
    rms,
    incident: null,
    karSlice: false,
    oneOfToEnum: true,
    refFilter: "all",
  });
  const jsonSchema = state?.jsonSchema ?? schemaHook.jsonSchema ?? undefined;

  // Seed the (render-stable) model ref from the fetch path exactly once;
  // the state flip guarantees the re-render the ref itself can't trigger.
  const [fetchedSeeded, setFetchedSeeded] = useState(false);

  // References for tracking easter eggs found in each narrative
  const narrativeEasterEggRefs = useRef<{
    [narrativeID: string]: React.MutableRefObject<boolean>;
  }>({});

  /**
   * @name checkAllEasterEggsFound
   *
   * @description Checks if all narratives' easter eggs have been found
   */
  const checkAllEasterEggsFound = () => {
    const model = incidentModelRef.current;
    // No model yet (fetch path still loading): keep the gate closed — a
    // null model must never read as "all easter eggs found".
    if (!model) return;
    if (!model.Narratives || model.Narratives.length === 0) {
      setAllEasterEggsFound(true);
      return;
    }

    const refs = Object.values(narrativeEasterEggRefs.current);
    const expectedCount = model.Narratives.length;
    const allFound =
      refs.length === expectedCount &&
      refs.every((ref) => ref?.current === true);

    setAllEasterEggsFound(allFound);
  };

  /**
   * @name handleEasterEggFound
   *
   * @description Callback when an easter egg is found - rechecks if all are found
   */
  const handleEasterEggFound = () => {
    checkAllEasterEggsFound();
  };

  // Check if there are any narratives when component mounts
  React.useEffect(() => {
    checkAllEasterEggsFound();
  }, []);

  // Fetch path: adopt the fetched incident once, then re-evaluate the
  // easter-egg gate against the now-present model.
  useEffect(() => {
    if (!incidentModelRef.current && fetchedModel) {
      incidentModelRef.current = fetchedModel;
      setFetchedSeeded(true);
      checkAllEasterEggsFound();
    }
  }, [fetchedModel]);

  // CO comments decide whether this is a resubmission. Only fetched when the
  // workflow is enabled — otherwise listComments returns 403. A comments
  // failure no longer locks the owner out (it used to ride the same
  // Promise.all as the ownership check); it just renders zero comments.
  useEffect(() => {
    if (!reportID || !coApprovalEnabled) {
      setCoComments([]);
      return;
    }
    let cancelled = false;
    getCoComments(reportID, { cycle: "current" })
      .then((fetched) => {
        if (!cancelled) setCoComments(fetched);
      })
      .catch((err) => {
        console.error("Error fetching CO comments:", err);
        if (!cancelled) setCoComments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [reportID, coApprovalEnabled]);

  // Hooks must run on every render before any conditional early-returns
  // below (`hasRequiredState`, `ownershipCheck === "pending" | "not-owner"`).
  // Moving these useMemos above the gates keeps the hook order stable.
  const rejectedSectionLabels = useMemo(
    () =>
      Array.from(
        new Set(
          coComments
            .filter((c) => c.isSectionRejection && c.fieldLabel)
            .map((c) => c.fieldLabel as string),
        ),
      ),
    [coComments],
  );
  const unresolvedCount = useMemo(
    () =>
      coComments.filter((c) => !c.parentCommentId && !c.resolvedAt).length,
    [coComments],
  );
  const memoizedSchemaMapping = useMemo(() => {
    // update schema mapping to have NarrativeViewWidget for narratives.
    // Deep clone to avoid mutating the original (the hook's mapping objects
    // are shared module singletons).
    const base = state?.schemaMapping ?? schemaHook.schemaMapping;
    if (!base) return null;
    const newSchemaMapping = JSON.parse(JSON.stringify(base));
    if (newSchemaMapping["Narratives"]?.properties?.["Text"]) {
      // schema mapping for eforce
      newSchemaMapping["Narratives"].properties["Text"].widget =
        "NarrativeViewWidget";
    } else if (newSchemaMapping["Narratives"]?.properties?.["Narrative"]) {
      // fallback schema mapping for IncidentUS (genericSchemaMapping)
      newSchemaMapping["Narratives"].properties["Narrative"].widget =
        "NarrativeViewWidget";
    }
    return newSchemaMapping;
  }, [state?.schemaMapping, schemaHook.schemaMapping]);

  // The signing content (banner + submit Paper) renders only once the model
  // ref is populated and the schema/mapping are in hand — on the fetch path
  // these arrive asynchronously.
  const modelReady = !!state?.modelData || fetchedSeeded;
  const contentReady = modelReady && !!jsonSchema && !!memoizedSchemaMapping;

  // Don't render the signing UI until ownership has been verified — otherwise
  // a non-owner briefly sees the officer-only sign CTA between mount and the
  // metadata fetch resolving.
  const gate = finalEditsGate({
    metadata: metadataQuery.data,
    isError: metadataQuery.isError,
    viewerUserId: user?.id,
  });
  if (gate === "loading") return null;

  // Non-owners (typically COs) can't access the officer's signing surface.
  // Route them to the CO review page if a reportID is available, otherwise home.
  if (gate === "not-owner") {
    const to = reportID ? `/co-review/${reportID}` : "/";
    return <Navigate to={to} replace />;
  }

  // Deep links to already-signed/committed reports go to the read-only
  // preview — this surface must not offer a second signature.
  if (gate === "wrong-status") {
    return <Navigate to={`/preview-report-info/${reportID}`} replace />;
  }

  // Fetch-path failures (the metadata error is already handled fail-closed
  // by the gate above).
  if (incidentQuery.isError || (!state?.jsonSchema && schemaHook.isError)) {
    return (
      <ErrorState
        variant="panel"
        message="An error occurred while fetching report information. Please try again later."
        onRetry={() => {
          if (incidentQuery.isError) void incidentQuery.refetch();
          if (schemaHook.isError) schemaHook.retry();
        }}
      />
    );
  }

  // Legacy direct-to-RMS path. Used only when the CO workflow flag is off —
  // when on, the CO performs the commit from the review page after approval.
  const handleRMSCommit = async (caseNumber?: string) => {
    if (isLoading) return;
    setOpenConfirmation(false);
    if (!reportID) {
      toast.error("Report ID is missing.");
      return;
    }
    const incident = incidentModelRef.current;
    if (!incident) {
      toast.error("No incident data available");
      return;
    }
    setIsLoading(true);
    try {
      const res = await rmsClient.post("/report/rms-commit", {
        reportId: parseInt(reportID),
        ...(caseNumber ? { caseNumber } : {}),
      });
      const eventNumber = res.data.eventNumber;
      navigate(
        `/confirmation/${reportID}` +
          (eventNumber ? `?eventNumber=${encodeURIComponent(eventNumber)}` : ""),
      );
    } catch (err) {
      console.error(err);
      toast.error(
        "An error occurred while submitting the report to the RMS servers. Please try again later.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitClick = (
    submitFunction: (caseNumber?: string) => void,
    mode: "sign" | "rmsCommit" = "sign",
  ) => {
    if (isLoading) return;
    handleSubmitFunction.current = submitFunction;
    setSubmitMode(mode);
    // Spec §5: when resubmitting after a CO rejection, ask the officer to confirm
    // they've addressed the comments before opening the standard submit confirmation.
    if (reportStatus === ReportStatus.CHANGES_REQUESTED) {
      setResubmitDialogOpen(true);
      return;
    }
    setOpenConfirmation(true);
  };

  const handleResubmitConfirm = () => {
    setResubmitDialogOpen(false);
    setOpenConfirmation(true);
  };

  const handleSignReport = async (caseNumber?: string) => {
    if (isLoading) return;
    setOpenConfirmation(false);
    if (!reportID) {
      toast.error("Report ID is missing.");
      return;
    }
    const incident = incidentModelRef.current;
    if (!incident) {
      toast.error("No incident data available");
      return;
    }
    setIsLoading(true);
    try {
      await rmsClient.post("/report/sign", {
        reportId: reportID,
        ...(caseNumber ? { caseNumber } : {}),
      });
      // The confirmation page derives its copy (awaiting-CO vs downloadable)
      // from the report's actual status now — no frozen message.
      navigate(`/confirmation/${reportID}`);
    } catch (err) {
      console.error(err);
      toast.error(
        "An error occurred while signing the report. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isRMSCommit = submitMode === "rmsCommit";

  return (
    <>
      {/* Page Header Removed */}
      {/* <Container ... /> */}

      <Portal>
        <Tooltip title="Return to editing page">
          <Fab
            variant={isLgUp ? "extended" : "circular"}
            // Explicit target, not navigate(-1): the page is deep-link/
            // refresh-safe now, so history -1 can be empty or not the editor.
            onClick={() => navigate(`/edit-section/${reportID}`)}
            disabled={isLoading}
            sx={{
              position: "fixed",
              top: isLgUp ? 70 : 130,
              right: 16,
              zIndex: 1000,
              bgcolor: theme.palette.primary.main,
              color: "white",
              border: "2px solid white",
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            <ArrowBackIcon />
            {isLgUp && "Back to Edit"}
          </Fab>
        </Tooltip>
      </Portal>

      <SignatureDialog
        open={openConfirmation}
        mode={isRMSCommit ? "rmsCommit" : "sign"}
        signerName={signerDisplayName}
        onClose={() => setOpenConfirmation(false)}
        onConfirm={handleSubmitFunction.current}
        requireCaseNumber={caseNumberInputEnabled}
        // D6: the accuracy attestation lives inside the signature dialog now
        // (was a page-level checkbox); copy unchanged, re-attested per open.
        requireAccuracyAttestation
      />
      <ResubmitConfirmationDialog
        open={resubmitDialogOpen}
        totalComments={
          coComments.filter((c) => !c.parentCommentId).length
        }
        unresolvedComments={unresolvedCount}
        rejectedSectionLabels={rejectedSectionLabels}
        onClose={() => setResubmitDialogOpen(false)}
        onConfirm={handleResubmitConfirm}
        loading={isLoading}
      />

      {/* Easter Egg Warning Banner */}
      {contentReady && !allEasterEggsFound && (
        <Container maxWidth="xl" sx={{ mb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "1.5rem",
                lineHeight: 1,
              }}
            >
              ⚠️
            </Typography>
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: theme.palette.warning.dark,
                  mb: 0.5,
                }}
              >
                Action Required: Find and Remove Easter Eggs
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                }}
              >
                Before you can confirm and submit this report, you must find and
                delete the hidden "easter egg" phrases in each narrative
                section. These are nonsensical phrases highlighted in orange.
                Click on the easter egg text and press{" "}
                <strong>Backspace</strong> or <strong>Delete</strong> to remove
                it.
              </Typography>
            </Box>
          </Paper>
        </Container>
      )}

      <ExtractedInfoPage
        isFinalReview
        incidentModelRef={incidentModelRef}
        narrativeEasterEggRefs={narrativeEasterEggRefs}
        onEasterEggFound={handleEasterEggFound}
        jsonSchema={jsonSchema}
        schemaMapping={memoizedSchemaMapping}
        stationId={organizationId ?? undefined}
      />

      {/* Confirmation and Submit Section (hidden until the model + schema
          are in hand — on the fetch path they arrive asynchronously) */}
      {contentReady && (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Action Buttons (the accuracy attestation moved into the
              signature dialog — D6) */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mt: 4,
              width: "100%",
            }}
          >
            {coApprovalEnabled ? (
              <>
                <Button
                  onClick={() => handleSubmitClick(handleSignReport)}
                  variant="contained"
                  disabled={
                    isLoading ||
                    !incidentModelRef.current ||
                    !allEasterEggsFound
                  }
                  loading={isLoading}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    boxShadow: `0 4px 14px ${alpha(
                      theme.palette.primary.main,
                      0.35,
                    )}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      boxShadow: `0 6px 20px ${alpha(
                        theme.palette.primary.main,
                        0.45,
                      )}`,
                      transform: "translateY(-2px)",
                    },
                    "&:disabled": {
                      background: theme.palette.grey[300],
                      color: theme.palette.grey[500],
                      boxShadow: "none",
                    },
                  }}
                >
                  {reportStatus === ReportStatus.CHANGES_REQUESTED
                    ? "Resubmit for CO Review"
                    : "Sign & Send to CO Review"}
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    textAlign: "center",
                    maxWidth: 520,
                  }}
                >
                  Your Commanding Officer will review the report, leave any
                  comments, and either approve it for RMS commit or send it
                  back for changes.
                </Typography>
              </>
            ) : (
              <>
                {showRMSCommit && (
                  <>
                    <Button
                      onClick={() => handleSubmitClick(handleRMSCommit, "rmsCommit")}
                      variant="contained"
                      disabled={
                        isLoading ||
                        !incidentModelRef.current ||
                        !allEasterEggsFound
                      }
                      loading={isLoading}
                      sx={{
                        py: 1.5,
                        px: 4,
                        borderRadius: 2.5,
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        textTransform: "none",
                        boxShadow: `0 4px 14px ${alpha(
                          theme.palette.primary.main,
                          0.35,
                        )}`,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: `0 6px 20px ${alpha(
                            theme.palette.primary.main,
                            0.45,
                          )}`,
                          transform: "translateY(-2px)",
                        },
                        "&:disabled": {
                          background: theme.palette.grey[300],
                          color: theme.palette.grey[500],
                          boxShadow: "none",
                        },
                      }}
                    >
                      Sign Report & Commit to RMS
                    </Button>
                    <Typography
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                      }}
                    >
                      or
                    </Typography>
                  </>
                )}
                <Button
                  onClick={() => handleSubmitClick(handleSignReport)}
                  variant={showRMSCommit ? "outlined" : "contained"}
                  disabled={
                    isLoading ||
                    !incidentModelRef.current ||
                    !allEasterEggsFound
                  }
                  loading={isLoading}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    ...(showRMSCommit
                      ? {
                          borderWidth: 2,
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          "&:hover": {
                            borderWidth: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                          },
                        }
                      : {
                          boxShadow: `0 4px 14px ${alpha(
                            theme.palette.primary.main,
                            0.35,
                          )}`,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            boxShadow: `0 6px 20px ${alpha(
                              theme.palette.primary.main,
                              0.45,
                            )}`,
                            transform: "translateY(-2px)",
                          },
                        }),
                    "&:disabled": {
                      background: showRMSCommit
                        ? "transparent"
                        : theme.palette.grey[300],
                      color: theme.palette.grey[500],
                      boxShadow: "none",
                      borderColor: theme.palette.grey[300],
                    },
                  }}
                >
                  Sign Report & Download
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Container>
      )}
    </>
  );
};
